import { useCreateAptStore } from "@elixir/stores";
import { useEffect } from "react";
import { ReactComponent as MagnifyingGlassRegularIcon } from "../../../assets/icons/font-awesome/magnifying-glass-regular.svg";
import { ReactComponent as UserRegularIcon } from "../../../assets/icons/font-awesome/user-regular.svg";
import useQueueStore from "../../../stores/queue-store";
import { Patient, TFormData } from "../../../types/appointment";
import {
  PatientProfileWithAbha,
  getPatientMainInfoString,
} from "../../../utils/appointment-utils";
import {
  AddPatientEvent,
  EKA_EVENT,
  EVENT_TYPES,
  IncomingEvents,
  PatientEventData,
} from "../../../utils/bridges";
import { showAddNewPatientPopover } from "../../../utils/bridges/methods";
import { getPatientNameText } from "../../../utils/generic-utils";
import { track } from "../../../utils/tracking";

export const FREE_LINE_ID = "free-line";

type TSelectedPatientProps = {
  selectedPatient?: PatientProfileWithAbha;
  formData?: TFormData[];
  showOnboardingArrows?: boolean | null;
};

const formatData = (data?: PatientEventData): PatientProfileWithAbha => {
  if (!data) return {} as PatientProfileWithAbha;

  // Construct full mobile number with country code if available
  const phoneCode = data?.personal?.phone?.c;
  const phoneNumber = data?.personal?.phone?.n;
  let mobile = phoneNumber || "";

  // If we have country code and mobile doesn't start with +, prepend it
  if (phoneCode && mobile && !mobile.startsWith("+")) {
    mobile = `${phoneCode}${mobile}`;
  }

  return {
    fln: getPatientNameText({
      fn: data?.personal?.name?.f,
      mn: "",
      ln: data?.personal?.name?.l,
    } as Patient),
    oid: data?.id,
    mobile: mobile,
    phone: phoneCode
      ? {
        code: phoneCode.startsWith("+") ? phoneCode : `+${phoneCode}`,
        number: phoneNumber,
      }
      : undefined,
    fn: data?.personal?.name?.f,
    mn: "",
    ln: data?.personal?.name?.l,
    dob: data?.personal?.age?.dob,
    gen: data?.personal?.gender,
    abha: data?.abha,
  } as PatientProfileWithAbha;
};

const SelectedPatient = ({
  selectedPatient,
  formData,
  showOnboardingArrows = false,
}: TSelectedPatientProps) => {
  const selectedDoctor = useCreateAptStore((state) => state.selectedDoctor);
  const setSelectedPatient = useQueueStore((state) => state.setSelectedPatient);
  const setSelectedInclusionIds = useCreateAptStore(
    (state) => state.setSelectedInclusionIds
  );

  const handleAddPatient: EventListener = (e: Event): void => {
    const { detail } = e as CustomEvent<IncomingEvents>;
    switch (detail.type) {
      case EVENT_TYPES.ADD_NEW_PT_APPT: {
        const { detail } = e as CustomEvent<AddPatientEvent>;
        const { data } = detail;
        setSelectedPatient(formatData(data));
        setSelectedInclusionIds([]);
      }
    }
  };

  useEffect(() => {
    window.addEventListener(EKA_EVENT, handleAddPatient);
    return () => {
      window.removeEventListener(EKA_EVENT, handleAddPatient);
    };
  }, []);

  return (
    <div className="dr-relative">
      <div className="dr-flex dr-items-center dr-space-x-16 dr-bg-darwin-touch-neutral-50 dr-p-12">
        {/* TODO: Do not remove popover - right now patient is added from the above patient list on click */}
        <div className="dr-flex dr-items-center dr-space-x-8 dr-min-w-70 dr-text-darwin-touch-neutral-1000">
          <UserRegularIcon className="dr-w-14 dr-min-w-14" />
          <div className="TouchCalloutRegular">Patient</div>
        </div>
        <div
          className={`dr-flex-1 dr-flex dr-items-center dr-justify-between BodyBody2Regular dr-rounded-8 dr-h-40 dr-min-h-40 dr-bg-darwin-touch-neutral-0 dr-px-12 ${selectedPatient
            ? "dr-text-darwin-touch-neutral-1000"
            : "dr-text-darwin-touch-primary-primary"
            }  dr-border-1 dr-border-solid dr-border-darwin-touch-neutral-200 dr-cursor-pointer`}
          onClick={() => {
            track("add_new_patient", {});
            showAddNewPatientPopover({ docId: selectedDoctor?.oid });
          }}
        >
          <div className="dr-line-clamp-1">
            {selectedPatient
              ? getPatientMainInfoString({
                patient: selectedPatient,
                formData,
              }).join(" | ")
              : "Select or Add Patient"}
          </div>
          <MagnifyingGlassRegularIcon className="dr-w-14 dr-h-14 dr-min-w-14 dr-min-h-14 dr-text-darwin-touch-primary-primary" />
        </div>
      </div>

      {showOnboardingArrows && (
        <div className="dr-flex dr-justify-center dr-absolute -dr-bottom-0 dr-right-80">
          <img
            src="/images/queue-page/free-line.webp"
            alt="free line"
            style={{ width: "58px", height: "10px" }}
            id={FREE_LINE_ID}
          />
        </div>
      )}
    </div>
  );
};

export default SelectedPatient;
